import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { graphGet } from '../../../api/graphClient';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';

// Shared Resource Mapping step for every non-M365 source (Excel/CSV, MPP
// XML, Monday, Smartsheet, Jira, Asana, ClickUp, Trello). Source assignees
// arrive as plain text; each distinct value is auto-resolved against Entra
// where a confident match exists (→ real dagrun_pm_assigneeid), and offered a
// manual PeoplePicker when the match is ambiguous or absent. Unmapped values
// import exactly as before — this step never blocks the import.

export type ResourceMap = Record<string, Person | null>;

interface Props {
  pca: IPublicClientApplication;
  connectionData: { headers?: string[]; rows?: unknown[][] } | null;
  mapping: Record<string, string>;   // source header → dagrun_pm field
  initial: ResourceMap;
  onContinue: (map: ResourceMap) => void;
}

interface GraphUser { id: string; displayName: string; mail: string | null; userPrincipalName: string | null; }
const toPerson = (u: GraphUser): Person => ({ aadObjectId: u.id, displayName: u.displayName, mail: u.mail, userPrincipalName: u.userPrincipalName });

/**
 * Resolve one assignee text value to a real Entra user. Confident when the
 * search yields an exact name/email/UPN hit, or a single unambiguous result.
 * Multiple non-exact hits (ambiguous) or none → not confident (manual picker).
 */
async function resolveNameToUser(pca: IPublicClientApplication, text: string): Promise<{ person: Person | null; confident: boolean }> {
  const t = text.trim();
  if (t.length < 2) return { person: null, confident: false };
  try {
    const encoded = encodeURIComponent(`"displayName:${t}" OR "mail:${t}" OR "userPrincipalName:${t}"`);
    const data = await graphGet<{ value: GraphUser[] }>(
      pca,
      `/users?$search=${encoded}&$select=id,displayName,mail,userPrincipalName&$top=10`,
      { ConsistencyLevel: 'eventual' },
    );
    const results = data.value ?? [];
    if (results.length === 0) return { person: null, confident: false };
    const lc = t.toLowerCase();
    const exact = results.find(u =>
      (u.displayName ?? '').toLowerCase() === lc ||
      (u.mail ?? '').toLowerCase() === lc ||
      (u.userPrincipalName ?? '').toLowerCase() === lc);
    if (exact) return { person: toPerson(exact), confident: true };
    if (results.length === 1) return { person: toPerson(results[0]), confident: true };
    return { person: null, confident: false }; // ambiguous
  } catch {
    return { person: null, confident: false };
  }
}

export default function ResourceMappingStep({ pca, connectionData, mapping, initial, onContinue }: Props) {
  const [map, setMap] = useState<ResourceMap>(initial);
  const [autoResolved, setAutoResolved] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState(false);
  const attempted = useRef<Set<string>>(new Set());

  // Distinct assignee text values from the column(s) mapped to
  // dagrun_pm_assigneename, with per-value task counts.
  const values = useMemo(() => {
    const headers = connectionData?.headers ?? [];
    const rows = connectionData?.rows ?? [];
    const assigneeCols = headers
      .map((h, i) => ({ h, i }))
      .filter(x => mapping[x.h] === 'dagrun_pm_assigneename')
      .map(x => x.i);
    const counts = new Map<string, number>();
    for (const row of rows) {
      for (const c of assigneeCols) {
        const v = row[c];
        if (v == null) continue;
        const s = String(v).trim();
        if (!s) continue;
        counts.set(s, (counts.get(s) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([text, count]) => ({ text, count }));
  }, [connectionData, mapping]);

  // Auto-resolve confident matches on mount / when the value set changes.
  // Runs once per distinct value; chunked to stay friendly to Graph.
  useEffect(() => {
    const todo = values.map(v => v.text).filter(t => !attempted.current.has(t) && !map[t]);
    if (todo.length === 0) return;
    todo.forEach(t => attempted.current.add(t));
    let cancelled = false;
    (async () => {
      setResolving(true);
      const CHUNK = 5;
      for (let i = 0; i < todo.length && !cancelled; i += CHUNK) {
        const batch = todo.slice(i, i + CHUNK);
        const settled = await Promise.all(batch.map(async t => ({ t, ...(await resolveNameToUser(pca, t)) })));
        if (cancelled) return;
        const hits = settled.filter(s => s.confident && s.person);
        if (hits.length) {
          setMap(prev => {
            const next = { ...prev };
            for (const h of hits) if (!next[h.t]) next[h.t] = h.person;
            return next;
          });
          setAutoResolved(prev => {
            const next = new Set(prev);
            for (const h of hits) next.add(h.t);
            return next;
          });
        }
      }
      if (!cancelled) setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [values]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedCount = values.filter(v => map[v.text]).length;

  return (
    <div>
      <h3>Map Resources to Microsoft 365 Users</h3>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16, maxWidth: 640 }}>
        Assignees in this source are plain text. We auto-match them to real Microsoft 365 users
        where we can (for avatars and @mentions on the imported tasks); resolve the rest below or
        leave them as text-only. Anything unresolved imports exactly as before — this step never
        blocks the import.
      </p>

      {values.length === 0 ? (
        <div style={{ padding: 24, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', color: 'var(--ink-3)', fontSize: 13, marginBottom: 20 }}>
          No assignee values detected in the source data
          {Object.values(mapping).includes('dagrun_pm_assigneename')
            ? ' — the mapped assignee column is empty.'
            : ' — no column is mapped to Assignee in the previous step.'}
          {' '}Nothing to map.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8, fontFamily: 'var(--font-mono)' }}>
            {resolvedCount} of {values.length} assignee{values.length === 1 ? '' : 's'} resolved to real users
            {resolving && <span style={{ color: 'var(--ink-4)' }}> · auto-matching…</span>}
          </div>
          <div style={{ border: '1px solid var(--border-light)', borderRadius: 'var(--r-md)', marginBottom: 20 }}>
            {values.map(v => {
              const auto = autoResolved.has(v.text);
              const mapped = !!map[v.text];
              return (
                <div key={v.text} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '10px 14px',
                  borderBottom: '0.5px solid var(--border-light)',
                }}>
                  <div style={{ flex: '0 0 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {mapped && (
                        <span title={auto ? 'Auto-matched to a Microsoft 365 user' : 'Resolved'} style={{ color: '#16A34A', fontSize: 13, flexShrink: 0 }}>✓</span>
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.text}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                      {v.count} task{v.count === 1 ? '' : 's'}{auto ? ' · auto-matched' : ''}
                    </div>
                  </div>
                  <span style={{ color: 'var(--ink-4)', fontSize: 13, flexShrink: 0 }}>→</span>
                  <div style={{ flex: 1, minWidth: 0, maxWidth: 340 }}>
                    <PeoplePicker
                      pca={pca}
                      value={map[v.text] ?? null}
                      onChange={p => {
                        setMap(prev => ({ ...prev, [v.text]: p }));
                        // A manual change overrides / clears the auto-match badge.
                        setAutoResolved(prev => { const n = new Set(prev); n.delete(v.text); return n; });
                      }}
                      placeholder="Search Microsoft 365 users… (optional)"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={() => onContinue(map)}>
          Continue{resolvedCount > 0 ? ` with ${resolvedCount} resolved` : ''}
        </button>
        {values.length > 0 && (
          <button className="btn-secondary" onClick={() => onContinue({})}>
            Skip — keep text-only assignees
          </button>
        )}
      </div>
    </div>
  );
}
